import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash, ShoppingCart as CartIcon } from '@phosphor-icons/react';

const CartPage = () => {
  const { cart, removeFromCart, loading } = useCart();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isEmpty = !cart?.items || cart.items.length === 0;

  return (
    <div className="min-h-screen bg-background py-12" data-testid="cart-page">
      <div className="container mx-auto px-4">
        <h1 
          className="text-4xl font-black text-primary mb-8"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Shopping Cart
        </h1>

        {isEmpty ? (
          <div className="text-center py-20 bg-white border border-border">
            <CartIcon size={64} className="text-text_muted mx-auto mb-4" />
            <p className="text-text_muted mb-6">Your cart is empty</p>
            <Link
              to="/plans"
              className="inline-block bg-primary text-white px-8 py-3 rounded font-medium hover:bg-opacity-90"
              data-testid="browse-plans-btn"
            >
              Browse Plans
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item, index) => (
                <div key={index} className="bg-white border border-border p-6" data-testid={`cart-item-${index}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-primary mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                        {item.plan_name}
                      </h3>
                      <div className="text-sm text-text_muted space-y-1">
                        <div>Format: <span className="font-medium text-primary">{item.config.file_type}</span></div>
                        <div>Drawing Sets: <span className="font-medium text-primary">{item.config.drawing_sets.join(', ')}</span></div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.plan_id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors"
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-border">
                    <span className="text-sm text-text_muted">Price:</span>
                    <span className="text-xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      R{item.total_price.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-border p-6 sticky top-24">
                <h3 className="text-xl font-bold text-primary mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Order Summary</h3>
                
                <div className="space-y-3 mb-6 pb-6 border-b border-border">
                  <div className="flex justify-between">
                    <span className="text-text_muted">Items:</span>
                    <span className="font-medium">{cart.items.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text_muted">Subtotal:</span>
                    <span className="font-medium">R{cart.total_amount.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-6">
                  <span className="text-lg font-bold">Total:</span>
                  <span className="text-2xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    R{cart.total_amount.toFixed(2)}
                  </span>
                </div>

                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-primary text-white py-3 rounded font-bold hover:bg-opacity-90 transition-colors"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;